import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Modal, Field } from "../pages/AdminCompaniesPage";
import BrainTypeSelect from "./BrainTypeSelect";
import { PencilSimple } from "@phosphor-icons/react";

/**
 * Info-tab van CoacheeDetailPage: toont de basisgegevens van de coachee
 * (naam, e-mail, breintype, status van de online test) en bevat de
 * "Bewerken"-modal. Roept `onSaved()` aan na een succesvolle PUT zodat
 * de parent zijn cache van de coachee kan verversen.
 */
export default function CoacheeInfoPanel({ coachee, onSaved }) {
  const [showEdit, setShowEdit] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brainType, setBrainType] = useState("");
  const [surveyEnabled, setSurveyEnabled] = useState(true);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const openEdit = () => {
    setName(coachee?.name || "");
    setEmail(coachee?.email || "");
    setBrainType(coachee?.brain_type || "");
    setSurveyEnabled(coachee?.survey_enabled !== false);
    setPassword("");
    setError("");
    setShowEdit(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setError("");
    const trimmedPassword = (password || "").trim();
    if (trimmedPassword && trimmedPassword.length < 6) {
      setError("Nieuw wachtwoord moet minimaal 6 tekens zijn");
      return;
    }
    const payload = {
      name, email, brain_type: brainType, survey_enabled: surveyEnabled,
    };
    if (trimmedPassword) payload.password = trimmedPassword;
    try {
      await api.put(`/coachees/${coachee.id}`, payload);
      setShowEdit(false);
      if (onSaved) onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <>
      <div className="max-w-2xl space-y-4" data-testid="coachee-info-view">
        <Row label="Naam" value={coachee.name} />
        <Row label="E-mailadres" value={coachee.email} />
        <Row label="Breintype" value={coachee.brain_type || "—"} mono />
        <Row label="Online breintype-test">
          {coachee.brain_type ? (
            <span
              className="inline-block px-2 py-0.5 text-xs uppercase tracking-wide font-semibold rounded-sm bg-[#43A047]/10 text-[#43A047] border border-[#43A047]/30"
              data-testid="online-status-finalized"
            >
              Definitief vastgesteld
            </span>
          ) : (
            <span className={
              "inline-block px-2 py-0.5 text-xs uppercase tracking-wide font-semibold rounded-sm " +
              (coachee.survey_enabled !== false
                ? "bg-[#1E88E5]/10 text-[#1E88E5] border border-[#1E88E5]/30"
                : "bg-zinc-100 text-zinc-500 border border-zinc-200")
            }>
              {coachee.survey_enabled !== false ? "Beschikbaar" : "Niet beschikbaar"}
            </span>
          )}
        </Row>
        <button
          onClick={openEdit}
          data-testid="edit-coachee-info"
          className="inline-flex items-center gap-2 mt-4 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
        >
          <PencilSimple size={16} /> Bewerken
        </button>
      </div>

      {showEdit && (
        <Modal title="Coachee bewerken" onClose={() => setShowEdit(false)}>
          <form onSubmit={onSave} className="space-y-4" data-testid="coachee-edit-form">
            <Field label="Naam" value={name} onChange={setName} testid="coachee-edit-name" />
            <Field label="E-mailadres" type="email" value={email} onChange={setEmail} testid="coachee-edit-email" />
            <BrainTypeSelect value={brainType} onChange={setBrainType} testid="coachee-edit-brain-type" label="Breintype" />
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">Online breintype-test</label>
              <label className="flex items-start gap-3 border-2 border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-zinc-400 transition-colors">
                <input
                  type="checkbox"
                  checked={surveyEnabled}
                  onChange={(e) => setSurveyEnabled(e.target.checked)}
                  data-testid="coachee-edit-survey-enabled"
                  className="mt-1 h-4 w-4 accent-zinc-900"
                />
                <div>
                  <div className="text-sm font-medium">Online test beschikbaar</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Zet uit als je het breintype handmatig instelt en de coachee geen online test moet maken.
                  </div>
                </div>
              </label>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5" htmlFor="coachee-edit-password">
                Nieuw wachtwoord
              </label>
              <input
                id="coachee-edit-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={6}
                placeholder="Laat leeg om niet te wijzigen"
                data-testid="coachee-edit-password"
                className="w-full border-2 border-zinc-200 rounded-sm px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none transition-colors"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Minimaal 6 tekens. Vul dit in als de coachee zijn wachtwoord is vergeten of je&#39;m handmatig wilt resetten.
              </p>
            </div>
            {error && <div className="text-sm text-[#E53935]">{error}</div>}
            <button type="submit" data-testid="coachee-edit-submit" className="w-full bg-zinc-900 text-white py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors">
              Opslaan
            </button>
          </form>
        </Modal>
      )}
    </>
  );
}

function Row({ label, value, mono, children }) {
  return (
    <div className="neuro-card p-4 flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={"text-sm font-medium " + (mono ? "font-mono" : "")}>{children ?? value}</div>
    </div>
  );
}
